import type { TFunction } from "i18next";
import React from "react";
import { Link } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import type { User } from "../../types/types";
import type {
  AttributeSchema,
  AttributeValue,
  ProductReference,
} from "../../types/types.PIM";
import { useAuthContext } from "../Auth";
import {
  buildObjectRefPreviewDisplayRows,
  getReferenceRestrictedMessage,
  getObjectReferenceDisplayHref,
  isObjectReferenceDisplayAccessible,
} from "./utils";
import type { ReferenceDisplayAccessContext } from "./utils";
import {
  CardProductCompact,
  NonClickableProductCard,
  ProductCardSingleWrapper,
} from "../CardProductCompact/CardProductCompact";
import { ObjectReferencePreviewTooltip } from "./ObjectReferencePreviewTooltip";
import { ProductRefEmblaCarousel } from "../ProductReference/ProductRefEmblaCarousel";
import { getProductReferenceHref } from "../ProductReference/ProductReferenceAttributeDisplay";

const InnerContentText = styled.p`
  color: ${({ theme }) => theme.primaryTextColor};
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
  margin: 0;
`;

const StyledUL = styled.ul.attrs({
  className: "object_reference_list",
})<{ $backgroundColor?: string }>`
  padding-left: ${({ theme }) => theme.resolve("spacing_md")};
  margin: 5px 0 10px;
  margin-left: 13px;
  li {
    font-size: ${({ theme }) => theme.fontSizes.medium};
    line-height: 1.33;
    padding-bottom: 5px;

    span,
    a {
      margin-left: -2px;
      position: relative;
      ${({ $backgroundColor }) =>
        $backgroundColor
          ? `
        background-color: ${$backgroundColor};
        border-radius: 2px;
        padding: 2px 4px;
      `
          : ""}
    }

    &::marker {
      font-size: ${({ theme }) => theme.fontSizes.regular};
    }
  }
`;

export function objectRefToProductReference(
  ref: NonNullable<AttributeValue["object_ref"]>
): ProductReference {
  return {
    id: ref.id,
    name: ref.name,
    slug: ref.slug ?? "",
    product_number: ref.object_number ?? "",
    image_url: ref.image_url ?? "",
    product_description: "",
    cas_number: "",
    brand_name: "",
    chemical_name: "",
    is_accessible: ref.is_accessible ?? false,
    is_visible_on_storefronts: ref.is_visible_on_storefronts ?? false,
    is_viewable: ref.is_viewable,
    pim_product_type: ref.pim_product_type,
    status: (ref.status as ProductReference["status"]) ?? "published",
    visibility_tenants: ref.visibility_tenants ?? [],
  };
}

export type ObjectReferenceAttributeSchema = Extract<
  AttributeSchema,
  { input_type: "object_reference" }
>;

export type ObjectReferenceAttributeDisplayProps = {
  attribute: ObjectReferenceAttributeSchema;
  isPortfolio: boolean;
  storePath: string;
  adminPath: string;
  user: User | null | undefined;
  roleIsSellerAdmin: boolean;
  backgroundColor?: string;
  t: TFunction;
};

function renderObjectRefListRow(
  value: AttributeValue,
  index: number,
  options: {
    isPortfolio: boolean;
    storePath: string;
    adminPath: string;
    user: User | null | undefined;
    roleIsSellerAdmin: boolean;
    backgroundColor?: string;
    t: TFunction;
    accessContext: ReferenceDisplayAccessContext;
  },
  accessibleValueIds?: Set<string>
): React.ReactNode {
  const { isPortfolio, storePath, adminPath, t, accessContext } = options;
  const ref = value.object_ref;
  if (!ref?.id) {
    return null;
  }

  const pathOpts = { isPortfolio, storePath, adminPath };
  const previewRows = buildObjectRefPreviewDisplayRows(ref, t);
  const previewAttrs = previewRows.map((r) => ({
    label: r.label,
    value: r.value,
  }));
  const href = getObjectReferenceDisplayHref(ref, pathOpts);
  const isAccessible =
    accessibleValueIds != null
      ? accessibleValueIds.has(value.id ?? "")
      : isObjectReferenceDisplayAccessible(ref, accessContext);

  const listItemKey = value.id || `object-ref-${index}`;

  if (!isAccessible) {
    const restrictedTip = getReferenceRestrictedMessage(ref, t);

    return (
      <li key={listItemKey}>
        <span data-tip={restrictedTip} data-for={value.id}>
          {ref.name}
        </span>
        <ReactTooltip delayHide={200} id={value.id} effect="solid" />
      </li>
    );
  }

  if (href) {
    return (
      <li key={listItemKey}>
        <ObjectReferencePreviewTooltip
          tooltipId={value.id}
          previewAttributes={previewAttrs}
        >
          <Link to={href}>{ref.name}</Link>
        </ObjectReferencePreviewTooltip>
      </li>
    );
  }

  return (
    <li key={listItemKey}>
      <ObjectReferencePreviewTooltip
        tooltipId={value.id}
        previewAttributes={previewAttrs}
      >
        <span>{ref.name}</span>
      </ObjectReferencePreviewTooltip>
    </li>
  );
}

function renderInaccessibleProductCard(
  ref: NonNullable<AttributeValue["object_ref"]>,
  t: TFunction
): React.ReactNode {
  return (
    <NonClickableProductCard>
      <CardProductCompact
        product={objectRefToProductReference(ref)}
        variant="tooltip"
        dynamicPreviewBlocks={[
          { header: "", content: getReferenceRestrictedMessage(ref, t) },
        ]}
      />
    </NonClickableProductCard>
  );
}

export function ObjectReferenceAttributeDisplay({
  attribute,
  isPortfolio,
  storePath,
  adminPath,
  user,
  roleIsSellerAdmin,
  backgroundColor,
  t,
}: ObjectReferenceAttributeDisplayProps) {
  const { hasPermission } = useAuthContext();
  if (!attribute.values?.length) {
    return <InnerContentText>--</InnerContentText>;
  }

  const hasCardDisplay =
    "display_configuration" in attribute &&
    attribute.display_configuration === "product_card";

  const valuesWithRef = attribute.values.filter((v) => v.object_ref?.id);

  if (!valuesWithRef.length) {
    return <InnerContentText>--</InnerContentText>;
  }
  const productTargets = valuesWithRef.filter(
    (v) => v.object_ref?.object_type === "product"
  );
  const entityTargets = valuesWithRef.filter(
    (v) => v.object_ref && v.object_ref.object_type !== "product"
  );
  const accessOpts: ReferenceDisplayAccessContext = {
    isPortfolio,
    user,
    roleIsSellerAdmin,
    hasPermission,
    targetObjectType: attribute.target_object_type,
  };
  const listOptions = {
    isPortfolio,
    storePath,
    adminPath,
    user,
    roleIsSellerAdmin,
    backgroundColor,
    t,
    accessContext: accessOpts,
  };

  const isValueAccessible = (v: AttributeValue): boolean => {
    if (!v.object_ref) return false;
    return isObjectReferenceDisplayAccessible(v.object_ref, accessOpts);
  };

  const accessibleValueIds = new Set(
    valuesWithRef
      .filter(isValueAccessible)
      .map((v) => v.id ?? "")
      .filter(Boolean)
  );

  const inaccessibleProductIds = new Set(
    productTargets
      .filter((v) => !isValueAccessible(v))
      .map((v) => v.object_ref!.id)
  );

  const getProductPreviewBlocks = (
    v: AttributeValue
  ): { header: string; content: string }[] => {
    const ref = v.object_ref!;
    if (inaccessibleProductIds.has(ref.id)) {
      return [
        {
          header: "",
          content: getReferenceRestrictedMessage(ref, t),
        },
      ];
    }
    return buildObjectRefPreviewDisplayRows(ref, t).map((r) => ({
      header: r.label,
      content: r.value,
    }));
  };

  const handleProductClick = (product: ProductReference) => {
    const productUrl = getProductReferenceHref(product, {
      isPortfolio,
      storePath,
      adminPath,
    });
    window.open(productUrl, "_blank", "noopener,noreferrer");
  };

  const productCardSection =
    hasCardDisplay && productTargets.length > 0 ? (
      productTargets.length === 1 && productTargets[0].object_ref ? (
        <ProductCardSingleWrapper>
          {inaccessibleProductIds.has(productTargets[0].object_ref.id)
            ? renderInaccessibleProductCard(productTargets[0].object_ref, t)
            : (() => {
                const ref = productTargets[0].object_ref!;
                const dynamicPreviewBlocks = getProductPreviewBlocks(
                  productTargets[0]
                );
                return (
                  <CardProductCompact
                    product={objectRefToProductReference(ref)}
                    onProductClick={handleProductClick}
                    dynamicPreviewBlocks={
                      dynamicPreviewBlocks.length > 0
                        ? dynamicPreviewBlocks
                        : undefined
                    }
                  />
                );
              })()}
        </ProductCardSingleWrapper>
      ) : (
        <ProductRefEmblaCarousel
          products={productTargets
            .map((v) =>
              v.object_ref && v.object_ref.object_type === "product"
                ? objectRefToProductReference(v.object_ref)
                : null
            )
            .filter((p): p is ProductReference => Boolean(p))}
          onProductClick={handleProductClick}
          inaccessibleProductIds={inaccessibleProductIds}
          dynamicPreviewBlocksByProductId={Object.fromEntries(
            productTargets
              .filter((v) => v.object_ref?.object_type === "product")
              .map((v) => [v.object_ref!.id, getProductPreviewBlocks(v)])
          )}
        />
      )
    ) : null;

  const entityListSection =
    entityTargets.length > 0 ? (
      <StyledUL $backgroundColor={backgroundColor}>
        {entityTargets.map((value, index) =>
          renderObjectRefListRow(value, index, listOptions, accessibleValueIds)
        )}
      </StyledUL>
    ) : null;

  if (hasCardDisplay && (productCardSection || entityListSection)) {
    return (
      <>
        {productCardSection}
        {entityListSection ? (
          <div style={{ marginTop: productCardSection ? 12 : 0 }}>
            {entityListSection}
          </div>
        ) : null}
      </>
    );
  }

  return (
    <StyledUL $backgroundColor={backgroundColor}>
      {valuesWithRef.map((value, index) =>
        renderObjectRefListRow(value, index, listOptions, accessibleValueIds)
      )}
    </StyledUL>
  );
}
